import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell, CheckCircle2, AlertTriangle, Info, TrendingUp, TrendingDown,
  ArrowRight, Check, Zap, FileText, RefreshCw, Loader2,
} from 'lucide-react';
import axios from 'axios';
import AppShell from '@/components/AppShell';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SEVERITY_CONFIG = {
  success: { bg: 'bg-[#1D6B4F]/8', border: 'border-[#1D6B4F]/20', icon: CheckCircle2, color: 'text-[#1D6B4F]' },
  warning: { bg: 'bg-[#EF9F27]/8', border: 'border-[#EF9F27]/20', icon: AlertTriangle, color: 'text-[#EF9F27]' },
  info: { bg: 'bg-[#1D6B4F]/8', border: 'border-[#1D6B4F]/20', icon: Info, color: 'text-[#1D6B4F]' },
};

const TYPE_ICONS = {
  score_change: TrendingUp,
  engine_fed: Zap,
  content_milestone: FileText,
  gap_progress: CheckCircle2,
  rescan_reminder: RefreshCw,
};

export default function Alerts() {
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const load = async () => {
      try {
        // Generate new alerts first, then fetch
        await axios.post(`${API}/alerts/generate`, {}, { withCredentials: true }).catch(() => {});
        const res = await axios.get(`${API}/alerts`, { withCredentials: true });
        setAlerts(res.data.alerts || []);
        setUnreadCount(res.data.unread_count || 0);
      } catch (e) {
        if (e.response?.status === 401) navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/alerts/mark-all-read`, {}, { withCredentials: true });
      setAlerts(prev => prev.map(a => ({ ...a, read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const handleAlertClick = async (alert) => {
    if (!alert.read) {
      try {
        await axios.put(`${API}/alerts/${alert.alert_id}/read`, {}, { withCredentials: true });
        setAlerts(prev => prev.map(a => a.alert_id === alert.alert_id ? { ...a, read: true } : a));
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch {}
    }
    if (alert.action_url) navigate(alert.action_url);
  };

  const handleRefresh = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/alerts/generate`, {}, { withCredentials: true });
      const res = await axios.get(`${API}/alerts`, { withCredentials: true });
      setAlerts(res.data.alerts || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch {}
    setGenerating(false);
  };

  const filtered = alerts.filter(a => {
    if (filter === 'unread') return !a.read;
    if (filter === 'score') return a.type === 'score_change';
    if (filter === 'engine') return a.type === 'engine_fed';
    return true;
  });

  const timeAgo = (dateStr) => {
    const d = new Date(dateStr);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F4EF] flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#D4C9B8] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <AppShell active="home">
      <div className="p-5 max-w-3xl mx-auto pb-28" data-testid="alerts-page">
        <div className="flex items-center justify-between mb-1">
          <h1 className="font-display text-2xl font-medium text-[#3C3226] tracking-tight">Monitoring Alerts</h1>
          <div className="flex items-center gap-2">
            <button
              onClick={handleRefresh}
              disabled={generating}
              className="p-2 rounded-sm hover:bg-[#EDE8DF] transition-colors"
              data-testid="refresh-alerts-btn"
            >
              {generating
                ? <Loader2 size={16} className="text-[#5C5347] animate-spin" />
                : <RefreshCw size={16} className="text-[#5C5347]" />}
            </button>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs font-medium text-[#1D6B4F] hover:text-[#16563F] transition-colors px-3 py-1.5 rounded-none hover:bg-[#1D6B4F]/5"
                data-testid="mark-all-read-btn"
              >
                Mark all read
              </button>
            )}
          </div>
        </div>
        <p className="text-sm text-[#5C5347] mb-4">
          {unreadCount > 0 ? `${unreadCount} unread alert${unreadCount > 1 ? 's' : ''}` : 'You\'re all caught up'}
        </p>

        {/* Filter tabs */}
        <div className="flex gap-1.5 mb-4 overflow-x-auto" data-testid="alert-filters">
          {[
            { key: 'all', label: 'All' },
            { key: 'unread', label: 'Unread' },
            { key: 'score', label: 'Score' },
            { key: 'engine', label: 'Engines' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-none text-xs font-medium border transition-all whitespace-nowrap ${
                filter === f.key
                  ? 'bg-[#3C3226] text-[#F0EBE3] border-[#3C3226]'
                  : 'bg-[#F7F4EF] text-[#5C5347] border-[#D4C9B8] hover:bg-[#EDE8DF]'
              }`}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* Alert list */}
        {filtered.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-14 h-14 rounded-none bg-[#F7F4EF] flex items-center justify-center mx-auto mb-3">
              <Bell size={24} className="text-[#5C5347]" />
            </div>
            <p className="text-sm font-medium text-[#3C3226] mb-1">No alerts yet</p>
            <p className="text-xs text-[#5C5347]">We'll notify you when something changes in your AI recommendations.</p>
          </div>
        ) : (
          <div className="space-y-2">
            <AnimatePresence>
              {filtered.map((alert, i) => {
                const sev = SEVERITY_CONFIG[alert.severity] || SEVERITY_CONFIG.info;
                const TypeIcon = TYPE_ICONS[alert.type] || Info;
                return (
                  <motion.button
                    key={alert.alert_id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.03 }}
                    onClick={() => handleAlertClick(alert)}
                    className={`w-full text-left p-4 rounded-none border transition-all hover:shadow-sm ${
                      alert.read ? 'bg-[#F7F4EF] border-[#D4C9B8]' : `${sev.bg} ${sev.border}`
                    }`}
                    data-testid={`alert-${alert.alert_id}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-8 h-8 rounded-none flex items-center justify-center flex-shrink-0 ${
                        alert.read ? 'bg-[#F7F4EF]' : `${sev.bg}`
                      }`}>
                        <TypeIcon size={15} className={alert.read ? 'text-[#5C5347]' : sev.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <p className={`text-sm font-medium ${alert.read ? 'text-[#5C5347]' : 'text-[#3C3226]'}`}>
                            {alert.title}
                          </p>
                          {!alert.read && <span className="w-2 h-2 rounded-none bg-[#C67A5C] flex-shrink-0" />}
                        </div>
                        <p className="text-xs text-[#5C5347] leading-relaxed">{alert.message}</p>
                        <p className="text-[10px] text-[#5C5347]/60 mt-1">{timeAgo(alert.created_at)}</p>
                      </div>
                      {alert.action_url && (
                        <ArrowRight size={14} className="text-[#5C5347]/40 flex-shrink-0 mt-2" />
                      )}
                    </div>
                  </motion.button>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </AppShell>
  );
}
